/**
 * Filter the page's currently visible events down to the ones that match a
 * stats-table group's key. Used by the page when opening the Classify
 * modal scoped to a single group: we hand the modal a sample of in-scope
 * events to drive `deriveRuleSuggestion`. The backend re-derives "events
 * in scope" from filter + key on the classify call (locked decision #13);
 * this is a frontend-side approximation for the rule-derivation step
 * only.
 *
 * Mirrors the dimension → event-field mapping used by the backend's group
 * key construction (159a route + the fixture-mode helper). Dimensions the
 * frontend cannot evaluate (`businessUnit`) match permissively.
 */

import type { GroupByDimension, WorkLogReviewEventRow } from '@/types/workLogReview';

function eventValueFor(e: WorkLogReviewEventRow, dim: string): string | null {
  switch (dim as GroupByDimension) {
    case 'app':
      return e.app;
    case 'appFileName':
      return e.appFileName;
    case 'title':
      return e.title;
    case 'rootDomain':
      return e.rootDomain;
    case 'site':
      return e.site;
    case 'url':
      return e.url;
    case 'employee':
      return e.employeeName;
    case 'project':
      return e.project?.name ?? null;
    case 'client':
      return e.project?.clientName ?? null;
    case 'classificationSource':
      return e.classificationSource;
    case 'classifiedByRule':
      return e.classifiedByRuleId;
    case 'day':
      return e.occurredAt.slice(0, 10);
    case 'hour':
      return e.occurredAt.slice(0, 13) + ':00';
    case 'businessUnit':
    case 'weekday':
      // The frontend event row doesn't carry these — defer to the server
      // when the modal applies (the server is the source of truth for the
      // in-scope event ids). Match permissively here so the derivation
      // sample isn't empty.
      return null;
    default:
      return null;
  }
}

export function matchesGroupKey(
  e: WorkLogReviewEventRow,
  key: Record<string, string | null>,
): boolean {
  for (const [dim, value] of Object.entries(key)) {
    const eventValue = eventValueFor(e, dim);
    // `null` keys match `null` event values exactly. For dimensions the
    // frontend can't resolve (returns null above), we accept any value.
    if (eventValue === null && value === null) continue;
    if (eventValue !== value) {
      // Permissive match for dims the frontend can't resolve: if our
      // lookup returned null but the key value is non-null, defer (don't
      // exclude). Otherwise mismatch.
      if (eventValue === null) continue;
      return false;
    }
  }
  return true;
}
